import axios from 'axios'
import { Maybe } from 'graphql/jsutils/Maybe'
import { decodeAccessToken, getAccessTokenFromClient } from '@/utils/auth-utils'
import { isDevelopmentEnvironment, isTestEnvironment } from '@/utils/environment-utils'
import { logger } from '@/utils/logging'
import { findHydraExternalId, hasHydraIdAndSlug } from '../ContentCatalog'
import { CreateOneToManyParams, LinkClaim, ShareLink } from './types'

const client = axios.create({
  baseURL: getBaseUrl(),
})

export async function createOneToManyShareLink({
  watchableTitle,
  baseUrl,
  episodeId,
}: CreateOneToManyParams): Promise<string | null> {
  const accessToken = getAccessTokenFromClient()
  const userId = decodeAccessToken(accessToken)?.userId
  logger().info('Attempting to create a share link', { watchableTitle, episodeId, userId })
  if (!hasHydraIdAndSlug(watchableTitle)) {
    logger().error(`This content catalog title is missing required hydra id or slug! A share link cannot be created.`, {
      watchableTitle,
    })
    return null
  }

  if (findHydraExternalId(watchableTitle) !== episodeId) {
    logger().error(
      `There is a mismatch between the hydra id obtained from hydra and the hydra id linked on the content catalog title record.`,
      { watchableTitle, episodeId },
    )
    return null
  }

  type CreatePayload = { contentId: string }
  type Response = Maybe<{ success: boolean; shareLink: Maybe<ShareLink> }>
  const result = await contentSharingPost<CreatePayload, Response>('/shareLink', accessToken, {
    contentId: watchableTitle.id,
  })

  if (!result) {
    logger().error('Share link creation failed: No response from API', { watchableTitle, episodeId, userId })
    return null
  }

  if (!result.success) {
    logger().error('Share link creation failed: API returned failure', {
      watchableTitle,
      episodeId,
      apiResponse: result,
      userId,
    })
    return null
  }

  if (!result.shareLink) {
    logger().error('Share link creation failed: No share link in response', {
      watchableTitle,
      episodeId,
      apiResponse: result,
      userId,
    })
    return null
  }

  const shareLink = result.shareLink
  const link = `${baseUrl}/watch/shared/${shareLink.id}`
  logger().info('Successfully created a share link', { watchableTitle, shareLink, link, userId })
  return link
}

interface GetShareLinkParams {
  id: string
}

export async function getShareLink({ id }: GetShareLinkParams): Promise<ShareLink | null> {
  type Response = Maybe<{ success: boolean; shareLink: Maybe<ShareLink> }>
  const result: Response = await contentSharingGet(`/shareLink/${id}`)

  if (!result || !result.success || !result.shareLink) return null

  logger().debug('Successfully fetched a share link', { shareLink: result.shareLink })
  return result.shareLink
}

interface ClaimOneToManyParams {
  accessToken: string
  shareLinkId: string
}

export async function claimOneToManyShareLink({
  accessToken,
  shareLinkId,
}: ClaimOneToManyParams): Promise<LinkClaim | null> {
  type Response = Maybe<{ success: boolean; claim: Maybe<LinkClaim> }>
  const result: Response = await contentSharingPost('/claim', accessToken, { shareLinkId })

  if (!result || !result.success || !result.claim) return null
  const claim = result.claim
  logger().info('Successfully claimed a share link', { shareLinkId, claim })
  return claim
}

async function contentSharingPost<T, R>(apiPath: string, accessToken: string, payload: T): Promise<R | null> {
  const start = Date.now()
  logger().info('User initiated share link creation', { payload, apiPath })
  try {
    const response = await client.post(apiPath, payload, { headers: getHeaders(accessToken) })

    if (!response) {
      logger().error(`Content Sharing API Post request failed with no response!`, {
        apiPath,
        payload,
        elapsed: Date.now() - start,
      })
      return null
    }

    return response.data
  } catch (err) {
    logger().error(`Content Sharing API Post request failed with an unknown error!`, {
      apiPath,
      payload,
      err,
      elapsed: Date.now() - start,
    })
    return null
  }
}

async function contentSharingGet<R>(apiPath: string, accessToken?: string): Promise<R | null> {
  const start = Date.now()
  try {
    const response = await client.get(apiPath, {
      headers: getHeaders(accessToken),
    })

    if (!response) {
      logger().error(`Content Sharing API Get request failed with no response.ok!`, {
        elapsed: Date.now() - start,
        apiPath,
      })
      return null
    }

    return response.data
  } catch (err) {
    logger().error(`Content Sharing API Get request failed with an unknown error!`, {
      apiPath,
      err,
      elapsed: Date.now() - start,
    })
    return null
  }
}

function getBaseUrl() {
  if (process.env.NEXT_PUBLIC_CONTENT_SHARING_API_BASE_URL === undefined) {
    if (isTestEnvironment() || isDevelopmentEnvironment()) {
      logger().warn('The required environment variable for the content sharing api is undefined!')
    } else {
      throw new Error('The required environment variable for the content sharing api is undefined!')
    }
  }
  return `${process.env.NEXT_PUBLIC_CONTENT_SHARING_API_BASE_URL}/api`
}

function getHeaders(accessToken: string | undefined) {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers.Authorization = accessToken
  return headers
}
